import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useUIStore } from '@/stores/uiStore';
import type { User } from '@/types/api';

export const SESSION_KEY = ['session'] as const;

type SessionResponse = { user: User | null };

// Session is server state, so it lives in TanStack Query (ADR-0009) rather than a
// Zustand store. GET /api/auth/session returns the user or null and also seeds the
// XSRF-TOKEN cookie for subsequent writes. Components read useSession().
export function useSession() {
  const query = useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => api.get<SessionResponse>('/auth/session'),
    // Don't retry the session probe — a failure just means "treat as guest".
    retry: false,
  });

  return {
    user: query.data?.user ?? null,
    isAuthenticated: Boolean(query.data?.user),
    isLoading: query.isLoading,
  };
}

// POST /api/auth/email-entry — existing email → magic-link; unknown email with name +
// host → access request. name/host are omitted in the form's login mode (email only).
// The response message is generic either way (no enumeration leak).
export function useEmailEntry() {
  return useMutation({
    mutationFn: (input: { email: string; name?: string; host?: string }) =>
      api.post<{ message: string }>('/auth/email-entry', input),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const setEditMode = useUIStore((state) => state.setEditMode);

  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      // Rediger never survives a logout (ADR-0023). Clear it, then drop the session.
      setEditMode(false);
      queryClient.setQueryData<SessionResponse>(SESSION_KEY, { user: null });
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });
}
